import { Button, Card, RadioGroup, TextButton, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { ALFRED_FORWARDING_MODES, type AlfredForwardingDialCodes, type AlfredForwardingMode, dialCodes } from '../../../lib/alfred-setup';
import { alfredTurnOffCodes, ltrIsolate } from '../../../lib/alfred-settings';
import styles from './AlfredForwardingPicker.module.css';

const COPY = 'connectors.channelsOut.alfred.';

/**
 * How a business line reaches Alfred: the two ways of forwarding as radio cards, then the hint — its title
 * beside Copy Code, the dial line, Verizon's code, and the landline / VoIP pointer. ONE component for both
 * places that offer it — the onboarding's "Forward your business calls" step (`AlfredSetupModal`) and the
 * settings screen's Call forwarding card (`AlfredSettings`) — so the cards, the codes and their Copy cannot
 * drift between them. Mirrored by iOS and Android `AlfredForwardingPicker`.
 *
 * It sets nothing: forwarding is dialled on the user's own phone, and the choice is never stored, so the
 * selection only decides which codes the hint shows. The settings screen adds the two things a returning
 * user comes for — the code that turns forwarding OFF, and, in place of the onboarding's ask-your-provider
 * pointer, the chat handoff for a landline or VoIP line, whose steps the carrier codes here do not cover.
 */
export function AlfredForwardingPicker({
  forwarding,
  mode,
  onModeChange,
  onCopyCode,
  ariaLabel,
  showTurnOff = false,
  onOtherPhoneSystem,
}: {
  forwarding: AlfredForwardingDialCodes | null;
  mode: AlfredForwardingMode;
  onModeChange: (mode: AlfredForwardingMode) => void;
  /** Called with the bare GSM code; the caller copies it and reports the click. */
  onCopyCode: (code: string) => void;
  ariaLabel: string;
  showTurnOff?: boolean;
  onOtherPhoneSystem?: () => void;
}) {
  const { t } = useTranslation();
  const codes = dialCodes(forwarding, mode);
  const turnOff = showTurnOff ? alfredTurnOffCodes(forwarding, mode) : null;

  return (
    <div className={styles.routing}>
      <RadioGroup
        variant="card"
        indicatorPlacement="leading"
        aria-label={ariaLabel}
        itemClassName={styles.option}
        value={mode}
        onChange={onModeChange}
        options={ALFRED_FORWARDING_MODES.map((option) => ({
          value: option,
          label: t(`${COPY}setup.routing.${option}.title`),
          description: t(`${COPY}setup.routing.${option}.description`),
        }))}
      />
      {codes && (
        <Card variant="outline" size="sm" className={styles.hint}>
          <div className={styles.hintHeader}>
            <Typography variant="h4" color="primary">
              {t(`${COPY}setup.routing.hintTitle`)}
            </Typography>
            <Button size="tiny" variant="outlined" onClick={() => onCopyCode(codes.code)}>
              {t(`${COPY}setup.routing.copyCode`)}
            </Button>
          </div>
          <Typography variant="p2" color="primary">
            {t(`${COPY}setup.routing.hint.dial`, { code: ltrIsolate(codes.code) })}
          </Typography>
          <Typography variant="p2" color="secondary">
            {t(`${COPY}setup.routing.hint.verizon`, { code: ltrIsolate(codes.verizonCode) })}
          </Typography>
          {onOtherPhoneSystem ? (
            <TextButton size="small" className={styles.other} onClick={onOtherPhoneSystem}>
              {t(`${COPY}settings.forwarding.other`)}
            </TextButton>
          ) : (
            <Typography variant="p2" color="secondary">
              {t(`${COPY}setup.routing.otherPhone`)}
            </Typography>
          )}
        </Card>
      )}
      {turnOff && (
        <Typography variant="p2" color="secondary">
          {t(`${COPY}settings.forwarding.off`, { code: ltrIsolate(turnOff.code), verizonCode: ltrIsolate(turnOff.verizonCode) })}
        </Typography>
      )}
    </div>
  );
}
